import FinancialAccount from "../FinancialAccount";
import { DateTime } from "luxon";
import { ASCENDANT_ORDER, DESCENDANT_ORDER } from "../../config";
import DateAndValue from "../DateAndValue";

export default class MonthlyBalanceSheetReport {
  constructor({
    dates,
    assets,
    liabilities,
    equity,
    dateOrder = DESCENDANT_ORDER,
  }) {
    this.dates = dates;
    this.assets = assets;
    this.liabilities = liabilities;
    this.equity = equity;
    this.dateOrder = dateOrder;
  }

  get ascendantDates() {
    return this.dates.sort((a, b) => {
      if (a > b) return 1;
      if (a < b) return -1;
    });
  }

  get descendantDates() {
    return this.dates.sort((a, b) => {
      if (a > b) return -1;
      if (a < b) return 1;
    });
  }

  get orderedDates() {
    if (this.dateOrder === ASCENDANT_ORDER) {
      return this.ascendantDates;
    }

    return this.descendantDates;
  }

  get yearsFormatted() {
    return this.getYearlyTotalAssets().map((obj) => obj.date);
  }

  get datesFormatted() {
    return this.orderedDates.map((date) => {
      return DateTime.fromISO(date).toFormat("LLLL y");
    });
  }

  get fixedAssetNames() {
    return this.getNames("assets", "Fixed Assets");
  }

  get currentAssetNames() {
    return this.getNames("assets", "Current Assets");
  }

  get currentLiabilityNames() {
    return this.getNames("liabilities", "Current Liabilities");
  }

  get longTermLiabilityNames() {
    return this.getNames("liabilities", "Long Term Liability");
  }

  get fixedAssetsCategoryNames() {
    return this.getCategories("assets", "Fixed Assets");
  }

  get currentAssetsCategoryNames() {
    return this.getCategories("assets", "Current Assets");
  }

  get currentLiabilitiesCategoryNames() {
    return this.getCategories("liabilities", "Current Liabilities");
  }

  get longTermLiabilityCategoryNames() {
    return this.getCategories("liabilities", "Long Term Liability");
  }

  get equityCategoryNames() {
    return this.getCategories("equity");
  }

  get equityNames() {
    const list = new Set();

    this.equity.forEach((item) => {
      list.add(item.name);
    });

    return Array.from(list);
  }

  get totalRealEstateAssets() {
    const realEstateAssets = this.getAssetsCategoryValues(
      FinancialAccount.getAllowedAssetsCategories().REAL_ESTATE_ASSET_CATEGORY
        .value
    );

    return this.orderedDates.map((date) => {
      const realEstateAsset = realEstateAssets.find(
        (liability) => liability.date === date
      );

      return {
        date,
        value: realEstateAsset?.value || 0,
      };
    });
  }

  get totalNetWorth() {
    const totalAssets = this.getTotalAssets();
    const totalLiabilities = this.getTotalLiabilities();

    return totalAssets?.map((asset) => {
      const liability = totalLiabilities.find((liability) => {
        return liability.date === asset.date;
      });

      return {
        ...asset,
        value: asset.value - liability.value,
      };
    });
  }

  get totalLiabilitiesAndNetWorth() {
    const totalLiabilities = this.getTotalLiabilities();

    return totalLiabilities?.map((liability) => {
      const netWorth = this.totalNetWorth.find((netWorth) => {
        return netWorth.date === liability.date;
      });

      return {
        ...liability,
        value: liability.value + netWorth.value,
      };
    });
  }

  get totalContributedCapital() {
    const {
      COMMON_STOCK_CATEGORY,
      PREFERRED_STOCK_CATEGORY,
      ADDITIONAL_PAID_IN_CAPITAL_CATEGORY,
      OWNER_AND_MEMBER_AND_PARTNER_CAPITAL_CATEGORY,
      TREASURY_STOCK_CATEGORY,
    } = FinancialAccount.getAllowedEquitiesCategories();
    const commonStockEquities = this.getEquitiesCategoryValues(
      COMMON_STOCK_CATEGORY.value
    );
    const preferredStockEquities = this.getEquitiesCategoryValues(
      PREFERRED_STOCK_CATEGORY.value
    );
    const additionalPaidInCapitalEquities = this.getEquitiesCategoryValues(
      ADDITIONAL_PAID_IN_CAPITAL_CATEGORY.value
    );
    const ownerAndMemberAndPartnerCapitalEquities =
      this.getEquitiesCategoryValues(
        OWNER_AND_MEMBER_AND_PARTNER_CAPITAL_CATEGORY.value
      );
    const treasuryStockEquities = this.getEquitiesCategoryValues(
      TREASURY_STOCK_CATEGORY.value
    );

    return this.orderedDates.map((date) => {
      const commonStockEquity =
        commonStockEquities.find((equity) => equity.date === date)?.value || 0;
      const preferredStockEquity =
        preferredStockEquities.find((equity) => equity.date === date)?.value ||
        0;
      const additionalPaidInCapitalEquity =
        additionalPaidInCapitalEquities.find((equity) => equity.date === date)
          ?.value || 0;
      const ownerAndMemberAndPartnerCapitalEquity =
        ownerAndMemberAndPartnerCapitalEquities.find(
          (equity) => equity.date === date
        )?.value || 0;
      const treasuryStockEquity =
        treasuryStockEquities.find((equity) => equity.date === date)?.value ||
        0;

      return {
        date,
        value:
          commonStockEquity +
          preferredStockEquity +
          additionalPaidInCapitalEquity +
          ownerAndMemberAndPartnerCapitalEquity -
          treasuryStockEquity,
      };
    });
  }

  get totalOwnerDistribution() {
    const dividendsEquities = this.getEquitiesCategoryValues(
      FinancialAccount.getAllowedEquitiesCategories().DIVIDENDS_CATEGORY.value
    );
    const ownersDistribution = this.getEquitiesCategoryValues(
      FinancialAccount.getAllowedEquitiesCategories()
        .OWNER_DISTRIBUTION_CATEGORY.value
    );

    return this.orderedDates.map((date) => {
      const dividendEquity = dividendsEquities.find(
        (dividend) => dividend.date === date
      );
      const ownerDistribution = ownersDistribution.find(
        (distribution) => distribution.date === date
      );
      return {
        date: date,
        value: (dividendEquity?.value || 0) + (ownerDistribution?.value || 0),
      };
    });
  }

  get totalRetainedEarnings() {
    const retainedEarnings = this.getEquitiesCategoryValues(
      FinancialAccount.getAllowedEquitiesCategories().RETAINED_EARNINGS_CATEGORY
        .value
    );

    return this.orderedDates.map((date) => {
      const retainedEarning = retainedEarnings.find(
        (earning) => earning.date === date
      );

      return {
        date,
        value: retainedEarning?.value || 0,
      };
    });
  }

  get totalNetIncome() {
    const netIncome = this.getEquitiesCategoryValues(
      FinancialAccount.getAllowedEquitiesCategories().NET_INCOME_CATEGORY.value
    );

    return this.orderedDates.map((date) => {
      const income = netIncome.find((income) => income.date === date);

      return {
        date,
        value: income?.value || 0,
      };
    });
  }

  getOrderedList(list) {
    if(list.length === 0) return this.orderedDates.map((date) => new DateAndValue({
      date: date,
      value: 0,
    }));  //we need it in order to avoid empty arrays

    if (this.dateOrder === ASCENDANT_ORDER) {
      return list.sort((a, b) => {
        if (a.date < b.date) return -1;
        if (a.date > b.date) return 1;
      });
    }

    return list.sort((a, b) => {
      if (a.date < b.date) return 1;
      if (a.date > b.date) return -1;
    });
  }

  getNames(propertyName, groupName) {
    const list = new Set();

    this[propertyName]
      .filter((group) => group.name === groupName)
      .forEach((item) => {
        item.items.forEach((item) => list.add(item.name));
      });

    return Array.from(list);
  }

  getCategories(propertyName, groupName) {
    const list = new Set();

    if (propertyName === "equity") {
      this.equity.forEach((item) => {
        list.add(item.category);
      });
    } else {
      this[propertyName]
        .filter((group) => group.name === groupName)
        .forEach((item) => {
          item.items.forEach((item) => list.add(item.category));
        });
    }

    //this new method is used for re-arranging the categories by liquidity
    let array;
    switch (propertyName) {
      case "assets":
        array =
          FinancialAccount.getAssetsCategoryOptions().filter((obj) => list.has(obj.value));
        break;

      case "liabilities":
        array =
          FinancialAccount.getLiabilitiesCategoryOptions().filter((obj) => list.has(obj.value));
        break;

      case "equity":
        array =
          FinancialAccount.getEquitiesCategoryOptions().filter((obj) => list.has(obj.value));
        break;

      default:
        break;
    }
    
    // This original code returns an array of raw ordered category array from the accounting data
    // const array = Array.from(list).map((category) => {
    //   let categoryObject = {};

    //   switch (propertyName) {
    //     case "assets":
    //       categoryObject =
    //         FinancialAccount.getAllowedAssetsCategories()[category];
    //       break;

    //     case "liabilities":
    //       categoryObject =
    //         FinancialAccount.getAllowedLiabilitiesCategories()[category];
    //       break;

    //     case "equity":
    //       categoryObject =
    //         FinancialAccount.getAllowedEquitiesCategories()[category];
    //       break;

    //     default:
    //       break;
    //   }

    //   return categoryObject;
    // });

    return array;
  }

  getItems(propertyName, groupName, itemName) {
    let balanceType = this[propertyName];
    if (groupName) {
      balanceType = this[propertyName].filter(
        (assetGroup) => assetGroup.name === groupName
      );
    }

    //there are missing(0) values for some months. We must give zero object and proper name, category, accountId for those months
    let result = this.dates.map((date) => new FinancialAccount({ date }));
    let name;
    let category;
    let accountId;

    balanceType.forEach((balanceItem) => {
      if (propertyName === "equity") {
        if (balanceItem.name === itemName) {
          result.filter((account) => account.date === balanceItem.date)[0].value = balanceItem.value;
          if(!name) name = balanceItem.name;
          if(!category) category = balanceItem.category;
          if(!accountId) accountId = balanceItem.accountId;
        }
        return;
      }

      balanceItem.items.forEach((item) => {
        if (item.name === itemName) {
          result.filter((account) => account.date === item.date)[0].value = item.value;
          if(!name) name = item.name;
          if(!category) category = item.category;
          if(!accountId) accountId = item.accountId;
        }
      });
    });

    result.forEach((item) => {
      item.name = name;
      item.category = category;
      item.accountId = accountId;
    })

    return this.getOrderedList(result);
  }

  getCategory({ name, type, groupName }) {
    let items = [];
    if (type === "assets" && groupName === "Fixed Assets") {
      items = this.getFixedAssetsByName(name);
    }

    if (type === "assets" && groupName === "Current Assets") {
      items = this.getCurrentAssetsByName(name);
    }

    if (type === "liabilities" && groupName === "Current Liabilities") {
      items = this.getCurrentLiabilities(name);
    }

    if (type === "liabilities" && groupName === "Long Term Liability") {
      items = this.getLongTermLiabilities(name);
    }

    if (type === "equity") {
      items = this.getEquityItems(name);
    }

    if (items.length === 0) return null;

    return items[0].category;
  }

  getFixedAssetsByName(assetName) {
    return this.getItems("assets", "Fixed Assets", assetName);
  }

  getCurrentAssetsByName(currentAssetName) {
    return this.getItems("assets", "Current Assets", currentAssetName);
  }

  getCurrentLiabilities(currentLiabilityName) {
    return this.getItems(
      "liabilities",
      "Current Liabilities",
      currentLiabilityName
    );
  }

  getLongTermLiabilities(longTermLiabilityName) {
    return this.getItems(
      "liabilities",
      "Long Term Liability",
      longTermLiabilityName
    );
  }

  getEquityItems(equityName) {
    return this.getItems("equity", "", equityName);
  }

  groupByDate(items) {
    const objectList = items.reduce((acc, obj) => {
      const date = obj.date;
      if (!acc[date]) {
        acc[date] = {
          date: date,
          value: 0,
        };
      }
      acc[date].value += obj.value;
      return acc;
    }, {});

    let result = this.dates.map((date) => new DateAndValue({ date, value: 0 }));    //this function is only used for monthly data
    Object.values(objectList).forEach((item) => {
      result.filter((account) => account.date === item.date)[0].value = item.value;
    });
    return this.getOrderedList(result);
  }

  groupByCategoryNameDate(items) {
    const result = items.reduce((acc, obj) => {
      const key = `${obj.date}-${obj.category}`;
      if (!acc[key]) {
        acc[key] = {
          date: obj.date,
          category: obj.category,
          value: 0,
        };
      }
      acc[key].value += obj.value;
      return acc;
    }, {});

    return Object.values(result);
  }

  getAssetsCategoryValues(category, groupName) {
    const allowedGroupValues = ["Fixed Assets", "Current Assets"];
    if (groupName && !allowedGroupValues.includes(groupName))
      throw new Error("groupName not valid");

    const items = [];

    if (!groupName) {
      this.assets.forEach((group) => {
        items.push(...group.items);
      });
    } else {
      this.assets
        .filter((assetGroup) => assetGroup.name === groupName)
        .forEach((group) => {
          items.push(...group.items);
        });
    }

    const list = this.groupByCategoryNameDate(items).filter(
      (item) => item.category === category
    );

    let result = this.dates.map((date) => new DateAndValue({ date, value: 0 }));    //this function is only used for monthly data
    list.forEach((item) => {
      result.filter((account) => account.date === item.date)[0].value = item.value;
    });
    return this.getOrderedList(result);
  }

  getLiabilitiesCategoryValues(category, groupName) {
    const items = [];
    if (!groupName) {
      this.liabilities.forEach((group) => {
        items.push(...group.items);
      });
    } else {
      this.liabilities
        .filter((liabilityGroup) => liabilityGroup.name === groupName)
        .forEach((group) => {
          items.push(...group.items);
        });
    }

    const list = this.groupByCategoryNameDate(items).filter(
      (item) => item.category === category
    );

    let result = this.dates.map((date) => new DateAndValue({ date, value: 0 }));    //this function is only used for monthly data
    list.forEach((item) => {
      result.filter((account) => account.date === item.date)[0].value = item.value;
    });
    return this.getOrderedList(result);
  }

  getEquitiesCategoryValues(category) {
    const list = this.groupByCategoryNameDate(this.equity).filter(
      (item) => item.category === category
    )
    
    let result = this.dates.map((date) => new DateAndValue({ date, value: 0 }));    //this function is only used for monthly data
    list.forEach((item) => {
      result.filter((account) => account.date === item.date)[0].value = item.value;
    });
    return this.getOrderedList(result);
  }

  getTotalAssets(groupName) {
    const list = [];
    if (!groupName) {
      this.assets.forEach((group) => {
        list.push(...group.items);
      });
    } else {
      this.assets
        .filter((assetGroup) => assetGroup.name === groupName)
        .forEach((group) => {
          list.push(...group.items);
        });
    }

    return this.groupByDate(list);
  }

  getTotalLiabilities(groupName) {
    const list = [];
    if (!groupName) {
      this.liabilities.forEach((group) => {
        list.push(...group.items);
      });
    } else {
      this.liabilities
        .filter((assetGroup) => assetGroup.name === groupName)
        .forEach((group) => {
          list.push(...group.items);
        });
    }
    return this.groupByDate(list);
  }

  getTotalEquities() {
    return this.groupByDate(this.equity);
  }

  getYearlyAssetsCategoryValues(category, groupName) {
    if(!groupName) return this.calculateTotalByYear(this.getAssetsCategoryValues(category));
    return this.calculateTotalByYear(this.getAssetsCategoryValues(category, groupName));
  }

  getYearlyLiabilitiesCategoryValues(category, groupName) {
    if(!groupName) return this.calculateTotalByYear(this.getLiabilitiesCategoryValues(category));
    return this.calculateTotalByYear(this.getLiabilitiesCategoryValues(category, groupName));
  }

  getYearlyEquitiesCategoryValues(category) {
    if(category === FinancialAccount.getAllowedEquitiesCategories().RETAINED_EARNINGS_CATEGORY.value) {
      return this.getArrayMinus(this.getYearlyTotalEquities(),this.getYearlyEquitiesCategoryValues(
        FinancialAccount.getAllowedEquitiesCategories().NET_INCOME_CATEGORY.value
      ));
    }
    else if(category === FinancialAccount.getAllowedEquitiesCategories().NET_INCOME_CATEGORY.value) {
      return this.calculateTotalByYear(this.getEquitiesCategoryValues(category), { sum: true });
    }
    else return this.calculateTotalByYear(this.getEquitiesCategoryValues(category));
  }

  getYearlyTotalAssets(groupName) {
    if(!groupName) return this.calculateTotalByYear(this.getTotalAssets());
    return this.calculateTotalByYear(this.getTotalAssets(groupName));
  }

  getYearlyTotalLiabilities(groupName) {
    if(!groupName) return this.calculateTotalByYear(this.getTotalLiabilities());
    return this.calculateTotalByYear(this.getTotalLiabilities(groupName));
  }
  
  getYearlyTotalEquities() {
    return this.calculateTotalByYear(this.getTotalEquities());
  }
  
  calculateTotalByYear(data, options = { sum: false }) {
    const yearTotalMap = {};
    let year = this.descendantDates[0];
    year = typeof year === "string" ? new Date(year) : year;
    if (this.dateOrder === "ASCENDANT_ORDER") data = data.reverse();

    for (const item of data) {
      const value = item.value;

      if (!isNaN(value)) {
        if (!yearTotalMap[year.toLocaleDateString()]) {
          yearTotalMap[year.toLocaleDateString()] = {
            sum: 0,
            count: 0,
          };
        }
        if(options.sum || yearTotalMap[year.toLocaleDateString()].count === 0)
          yearTotalMap[year.toLocaleDateString()].sum += value;
          //we get the sum of 12 months if option.sum is true, otherwise, get the last month
        yearTotalMap[year.toLocaleDateString()].count++;
        if (yearTotalMap[year.toLocaleDateString()].count === 12)
          year.setFullYear(year.getFullYear() - 1);
      }
    }

    const result = Object.entries(yearTotalMap).map(
      ([year, { sum, count }]) => ({
        date: DateTime.fromJSDate(new Date(year)).toFormat("LLLL y"),
        value: sum,
      })
    );
    
    return this.getOrderedList(result);
  }

  getArrayMinus(array1, array2) {
    //add element values of arrays
    return this.yearsFormatted.map(
      (year, index) =>
        new DateAndValue({
          date: year,
          value: array1[index].value - array2[index].value,
        })
    );
  }
}
